/**
 * zalo-quota-line.tsx — Hạn mức Zalo của một tài khoản cá nhân, ngay dưới thẻ.
 *
 * Hiện đúng ba con số nhân viên cần để không bị Zalo chặn giữa chừng:
 * lời mời kết bạn hôm nay, lượt trò chuyện với người lạ tháng này, sức chứa
 * danh bạ. Chuyển màu khi gần hết (≥ 80%) để kịp đổi cách làm TRƯỚC khi hết,
 * vì hết rồi thì Zalo mới báo — lúc đó khách đang chờ trả lời.
 *
 * Gói tài khoản do người dùng tự khai: hệ thống không đọc được gói từ Zalo,
 * mà gói lại quyết định hạn mức người lạ và danh bạ.
 */
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { cn, formatNumber } from '@/lib/utils'
import { apiError } from '@/lib/api-client'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useZaloRateStatus, useSetZaloTier, ZALO_TIER_OPTIONS, type ZaloTier } from '@/hooks/use-integrations'
import { ZaloProxyButton } from './zalo-proxy-dialog'
import { useMyPermissions } from '@/hooks/use-settings'

function tone(used: number, limit: number | null): string {
  if (limit === null || limit <= 0) return 'text-muted-foreground'
  const r = used / limit
  if (r >= 1) return 'font-medium text-destructive'
  if (r >= 0.8) return 'font-medium text-warning'
  return 'text-muted-foreground'
}

function Chi({ label, used, limit, title }: { label: string; used: number; limit: number | null; title: string }) {
  return (
    <span className={cn('whitespace-nowrap', tone(used, limit))} title={title}>
      {label} {formatNumber(used)}
      {limit !== null ? `/${formatNumber(limit)}` : ''}
    </span>
  )
}

export function ZaloQuotaLine({ accountId }: { accountId: string }) {
  const { t } = useTranslation('chanZalo')
  const { data } = useZaloRateStatus(accountId)
  const setTier = useSetZaloTier()
  // Khai gói = PUT /zalo-accounts/:id/tier → cần `integrations.update`; thiếu quyền thì chỉ hiện tên gói.
  const canUpdate = !!useMyPermissions().data?.has('integrations.update')
  if (!data) return null

  const kb = data.categories.friend_request
  const td = data.categories.automation_proactive

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
      {canUpdate ? (
        <Select
          value={data.tier}
          onValueChange={(v) =>
            setTier.mutate(
              { accountId, tier: v as ZaloTier },
              {
                onSuccess: (s) => toast.success(t('quota.tierChanged', { tier: s.tierLabel })),
                onError: (e) => toast.error(apiError(e)),
              },
            )
          }
        >
          <SelectTrigger className="h-6 w-auto gap-1 px-2 text-xs" title={t('quota.tierTitle')}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ZALO_TIER_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value} className="text-xs">
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : (
        <span className="text-muted-foreground" title={t('quota.tierTitle')}>
          {data.tierLabel}
        </span>
      )}

      {kb && (
        <Chi
          label={t('quota.friendLabel')}
          used={kb.used}
          limit={kb.dailyLimit}
          title={t('quota.friendTitle')}
        />
      )}

      {data.strangers.friendsSynced ? (
        <Chi
          label={t('quota.strangerLabel')}
          used={data.strangers.used}
          limit={data.strangers.quota}
          title={
            data.strangers.quota === null
              ? t('quota.strangerTitleNoQuota')
              : t('quota.strangerTitleQuota')
          }
        />
      ) : (
        <span className="text-muted-foreground" title={t('quota.notSyncedTitle')}>
          {t('quota.notSynced')}
        </span>
      )}

      {td && (
        <Chi
          label={t('quota.autoLabel')}
          used={td.used}
          limit={td.dailyLimit}
          title={t('quota.autoTitle')}
        />
      )}

      <ZaloProxyButton accountId={accountId} />

      <Chi
        label={t('quota.contactsLabel')}
        used={data.contacts.count}
        limit={data.contacts.cap}
        title={t('quota.contactsTitle')}
      />
    </div>
  )
}
